
import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Mail, Key, LogIn, UserPlus, Eye, EyeOff } from "lucide-react";
import { useSession } from "@supabase/auth-helpers-react";
import { Button } from "@/components/ui/button";

const AuthPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  
  // Get the return path from location state, default to dashboard
  const returnTo = location.state?.returnTo || "/dashboard";
  
  // Check if we should start in signup mode
  const [isSignUp, setIsSignUp] = useState(location.state?.signup || false);

  useEffect(() => {
    if (session) {
      navigate(returnTo, { replace: true });
    }
  }, [session, navigate, returnTo]);

  const validateInputs = () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!password || password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return false;
    }
    return true;
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateInputs()) return;
    
    setIsLoading(true);

    try {
      if (isSignUp) {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback`,
          }
        });
        
        if (error) {
          if (error.message.includes('User already registered')) {
            toast.error('An account with this email already exists. Please sign in instead.');
          } else {
            throw error;
          }
          return;
        }
        toast.success("Account created successfully! Please check your email to verify your account.");
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (error) {
          if (error.message.includes('Invalid login credentials')) {
            toast.error('Invalid email or password. Please try again.');
          } else if (error.message.includes('Email not confirmed')) {
            toast.error('Please verify your email address before signing in.');
          } else {
            throw error;
          }
          return;
        }
        toast.success("Logged in successfully!");
        navigate(returnTo, { replace: true });
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      toast.error(error.message || 'An error occurred during authentication');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialAuth = async (provider: 'google') => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback?returnTo=${encodeURIComponent(returnTo)}`,
        },
      });
      
      if (error) throw error;
      
    } catch (error: any) {
      console.error(`Error signing in with ${provider}:`, error);
      toast.error(error.message || `Failed to sign in with ${provider}`);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 bg-card-bg border-border">
        <div className="flex justify-between items-center mb-6">
          <Link to="/" className="text-primary hover:text-primary-hover">
            ← Back to Home
          </Link>
        </div>
        
        <div className="flex flex-col space-y-2 mb-8">
          <h1 className="text-2xl font-bold text-center text-text-primary">
            {isSignUp ? "Create an Account" : "Welcome Back"}
          </h1>
          <p className="text-center text-text-secondary">
            {isSignUp 
              ? "Sign up to start tracking your career progress"
              : "Sign in to continue your career journey"
            }
          </p>
        </div>
        <form onSubmit={handleAuth} className="space-y-6">
          <div className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-5 w-5 text-text-muted" />
              <Input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 bg-card-bg border-border text-text-primary placeholder:text-text-secondary h-12"
                required
                disabled={isLoading}
              />
            </div>
            <div className="relative">
              <Key className="absolute left-3 top-3 h-5 w-5 text-text-muted" />
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10 bg-card-bg border-border text-text-primary placeholder:text-text-secondary h-12"
                required
                disabled={isLoading}
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-text-muted hover:text-text-primary transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
          <button
            type="submit"
            className="w-full bg-primary hover:bg-primary-hover text-white rounded-lg py-3 flex items-center justify-center space-x-2 transition-all duration-200 h-12"
            disabled={isLoading}
          >
            {isLoading ? (
              "Please wait..."
            ) : isSignUp ? (
              <>
                <UserPlus className="h-5 w-5" />
                <span>Sign Up</span>
              </>
            ) : (
              <>
                <LogIn className="h-5 w-5" />
                <span>Sign In</span>
              </>
            )}
          </button>
          
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card-bg px-2 text-text-muted">Or</span>
            <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-border"></div>
          </div>
          
          <div className="mt-6 text-center space-y-4">
            <button
              type="button"
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-sm text-primary hover:text-primary-hover transition-colors"
              disabled={isLoading}
            >
              {isSignUp
                ? "Already have an account? Sign in"
                : "Don't have an account? Sign up"}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AuthPage;
